import styled from 'styled-components'

import { Button, DataTable, Input, Select } from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { NoDataComponent } from '@/pages/bizMgmt/bizMgmt'
import { tableStyles } from '@/styles/dataTable/style'
import React, { useEffect, useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import numberUtils from '@/utils/numberUtils'
import commonUtils from '@/utils/commonUtils'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'

interface DataRow {
  bizCd?: string //업체코드
  bizNm?: string //업체명
  seq?: string //순번
  chngDt?: string //변경일자
  glfPrceCd?: string //골프장가격코드
  glfPrceNm?: string //골프장가격명
  curr?: string //통화단위
  oldPrce?: string //이전가격
  newPrce?: string //신규가격
  updtYn?: string //업데이트 여부
  baseDt?: string //기준일자
  rem?: string //비고
  statInclYn?: string //통계포함여부
  updId?: string
  updDate?: string
}

const PriceHistMgmt: React.FC = () => {
  const axios = useAxios()

  const navigate = useNavigate()
  const location = useLocation()

  const { showAlert, showConfirm } = useDialog()

  const [data, setData] = useState<DataRow[]>([])

  useEffect(() => {
    getPriceHist()
  }, [])

  //이력조회
  const getPriceHist = async () => {
    try {
      const detailData = {
        bizCd: location.state?.bizCd,
        glfPrceCd: location.state?.glfPrceCd,
      }

      const response = await axios.get('/priceMgmt/getPriceHistList', {
        params: { ...detailData },
      })

      const histList = response.data

      if (Array.isArray(histList)) {
        setData(histList)
      }
    } catch (err: any) {
      console.error('༼ つ ◕_◕ ༽つ Error fetching priceManage:', err)
    }
  }

  const handleSave = (row: DataRow) => {
    // const rowIndex = data.findIndex((d) => d.seq === row.seq)
    // const rowToSave = data[rowIndex]
    fetchSave(row)
  }

  const fetchSave = async (rowToSave: DataRow) => {
    if (rowToSave) {
      rowToSave.bizCd = location.state?.bizCd

      if (!rowToSave?.bizCd) {
        showAlert({
          title: '업체코드가 없습니다. 비정상적인 접근. 다시 시도해주세요.',
        })
        return
      }

      if (!rowToSave?.glfPrceCd) {
        showAlert({
          title: '가격코드가 없습니다. 비정상적인 접근. 다시 시도해주세요.',
        })
        return
      }

      if (!rowToSave?.newPrce) {
        showAlert({ title: '가격을 입력해주세요.' })
        return
      }

      if (!rowToSave?.baseDt) {
        showAlert({ title: '기준일자를 입력해주세요.' })
        return
      }

      if (!rowToSave?.updtYn) {
        showAlert({ title: '업데이트여부를 입력해주세요.' })
        return
      }

      try {
        const response = await axios.post(
          '/priceMgmt/updateGlfPriceHist',
          rowToSave,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        //TODO. API에서 성공여부 코드로 보내기
        if (response.status === 200) {
          showAlert({
            type: 'success',
            title: '저장을 성공했어요.',
            onClose() {
              getPriceHist()
            },
          })
        } else {
          showAlert({
            title: '저장을 실패했어요. 잠시 후 다시 시도해 주세요.',
          })
        }
      } catch (err: any) {
        showAlert({
          title: '오류가 발생했어요. 잠시 후 다시 시도해주세요.',
        })
        console.error('Error updating biz:', err)
      }
    } else {
      console.log('저장된 데이터가 없습니다.')
      return
    }
  }

  const handleDelete = (row: DataRow) => {
    // const rowIndex = data.findIndex((d) => d.seq === row.seq)
    // const rowToSave = data[rowIndex]
    fetchDelete(row)
  }

  const fetchDelete = async (rowToSave: DataRow) => {
    if (rowToSave) {
      rowToSave.bizCd = location.state?.bizCd

      if (!rowToSave?.bizCd) {
        showAlert({
          title: '업체코드가 없습니다. 비정상적인 접근. 다시 시도해주세요.',
        })
        return
      }

      if (!rowToSave?.glfPrceCd) {
        showAlert({
          title: '가격코드가 없습니다. 비정상적인 접근. 다시 시도해주세요.',
        })
        return
      }

      try {
        const response = await axios.post(
          '/priceMgmt/deleteGlfPriceHist',
          rowToSave,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        //TODO. API에서 성공여부 코드로 보내기
        if (response.status === 200) {
          showAlert({
            type: 'success',
            title: '삭제되었습니다.',
            onClose() {
              getPriceHist()
            },
          })
        } else {
          showAlert({
            title: '삭제에 실패했어요. 잠시 후 다시 시도해 주세요.',
          })
        }
      } catch (err: any) {
        showAlert({
          title: '오류가 발생했어요. 잠시 후 다시 시도해주세요.',
        })
        console.error('Error deleteing biz:', err)
      }
    } else {
      console.log('삭제된 데이터가 없습니다.')
      return
    }
  }

  // 특정 셀 값 변경 함수
  const handleInputChange = (
    row: DataRow,
    field: keyof DataRow,
    value: string,
  ) => {
    let newValue = value

    if (field === 'oldPrce' || field === 'newPrce') {
      newValue = newValue.replace(/[^0-9]/g, '') // 숫자 외 문자 제거
    }

    const newData = [...data]
    const rowIndex = newData.findIndex((d) => d.seq === row.seq)
    if (rowIndex !== -1) {
      newData[rowIndex] = { ...newData[rowIndex], [field]: newValue }
      setData(newData)
    }
  }

  const columns: TableColumn<DataRow>[] = [
    {
      name: 'SEQ',
      selector: (row) => row.seq || '',
      sortable: true,
    },
    {
      name: '가격코드',
      selector: (row) => row.glfPrceNm || '',
      sortable: true,
    },
    {
      name: '통화단위',
      selector: (row) => row.curr || 'KRW',
      cell: (row, index) => (
        <Input
          id="curr"
          name="curr"
          type="text"
          value={row.curr}
          onChange={(e) => handleInputChange(row, 'curr', e.target.value)}
          style={{ textAlign: 'center' }}
        />
      ),
    },
    {
      name: '이전가격',
      selector: (row) => row.oldPrce || '',
      cell: (row, index, column, id) => (
        <Input
          id="oldPrce"
          name="oldPrce"
          type="text"
          value={numberUtils.formatNumberWithCommas(row.oldPrce ?? '')}
          // onBlur={(e) => {
          //   const formatted = numberUtils.formatNumberWithCommas(
          //     Number(e.target.value.replace(/[^0-9]/g, '')),
          //   )
          //   const newData = [...data]
          //   const rowIndex = newData.findIndex((d) => d.seq === row.seq)
          //   if (rowIndex !== -1) {
          //     newData[rowIndex].oldPrce = formatted
          //     setData(newData)
          //   }
          // }}
          onChange={(e) => {
            const rawValue = e.target.value.replace(/[^0-9]/g, '')
            const newData = [...data]
            const rowIndex = newData.findIndex((d) => d.seq === row.seq)
            if (rowIndex !== -1) {
              newData[rowIndex].oldPrce = rawValue
              setData(newData)
            }
          }}
          style={{ textAlign: 'right' }}
        />
      ),
    },
    {
      name: '신규가격',
      selector: (row) => row.newPrce || '',
      cell: (row) => (
        <Input
          id="newPrce"
          name="newPrce"
          type="text"
          value={numberUtils.formatNumberWithCommas(row.newPrce ?? '')}
          // onBlur={(e) => {
          //   const formatted = numberUtils.formatNumberWithCommas(
          //     Number(e.target.value.replace(/[^0-9]/g, '')),
          //   )
          //   const newData = [...data]
          //   const rowIndex = newData.findIndex((d) => d.seq === row.seq)
          //   if (rowIndex !== -1) {
          //     newData[rowIndex].newPrce = formatted
          //     setData(newData)
          //   }
          // }}
          onChange={(e) => {
            const rawValue = e.target.value.replace(/[^0-9]/g, '')
            const newData = [...data]
            const rowIndex = newData.findIndex((d) => d.seq === row.seq)
            if (rowIndex !== -1) {
              newData[rowIndex].newPrce = rawValue
              setData(newData)
            }
          }}
          style={{ textAlign: 'right' }}
        />
      ),
    },
    {
      name: '기준일자',
      selector: (row) => row.baseDt || '',
      cell: (row, index) => (
        <Input
          id="baseDt"
          name="baseDt"
          type="text"
          value={row.baseDt}
          onChange={(e) => handleInputChange(row, 'baseDt', e.target.value)}
          style={{ textAlign: 'center' }}
        />
      ),
      sortable: true,
    },
    {
      name: '업데이트여부',
      selector: (row) => row.updtYn || '',
      cell: (row, index) => (
        <Select
          id="updtYn"
          name="updtYn"
          onChange={(e) => handleInputChange(row, 'updtYn', e.target.value)}
          value={row.updtYn || ''}
        >
          <option value=""></option>
          <option value="Y">Y</option>
          <option value="N">N</option>
        </Select>
      ),
      width: '80px',
    },
    {
      name: '비고',
      selector: (row) => row.rem || '',
    },
    {
      name: '통계포함여부',
      selector: (row) => row.statInclYn || '',
      cell: (row, index) => (
        <Select
          id="statInclYn"
          name="statInclYn"
          onChange={(e) => handleInputChange(row, 'statInclYn', e.target.value)}
          value={row.statInclYn || ''}
        >
          <option value=""></option>
          <option value="Y">포함</option>
          <option value="N">미포함</option>
        </Select>
      ),
      width: '80px',
    },
    {
      name: '수정일시',
      center: true,
      selector: (row) => commonUtils.formatDate(row.updDate || '', true),
    },
    {
      name: '',
      center: true,
      cell: (row, index) => (
        <div style={{ display: 'flex', gap: '15px' }}>
          {' '}
          {/* 간격 조정 */}
          <Button
            color="neverbe"
            variant="outlined"
            onClick={() => handleSave(row)}
          >
            저장
          </Button>
          <Button
            color="neverbe"
            variant="outlined"
            onClick={() =>
              showConfirm({
                title: '삭제하시겠습니까?',
                message: '업체코드 : ' + row.bizCd + '__ SEQ : ' + row.seq,
                onConfirm() {
                  handleDelete(row)
                },
              })
            }
          >
            삭제
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <div style={{ marginLeft: '20px', fontSize: '20px' }}>
          가격이력관리
          <br />
          <span
            style={{ fontSize: '18px', cursor: 'pointer' }}
            onClick={() => window.open(location.state?.chrgeDomain, '_blank')}
          >
            {'업체명 : '}
            {data.length > 0 ? data[0].bizNm || data[3].bizNm : ''}
          </span>
          <br />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="outlined"
              color="neverbe"
              onClick={() =>
                // showConfirm({
                //   title: '가격관리 화면으로 이동할까요?',
                //   message: location.state?.bizCd,
                //   onConfirm() {
                //     navigate('/bizMgmt/priceMgmt', {
                //       state: {
                //         bizCd: location.state?.bizCd,
                //         bizSearchState: location.state?.bizSearchState,
                //       },
                //     })
                //   },
                // })
                navigate('/bizMgmt/priceMgmt', {
                  state: {
                    bizCd: location.state?.bizCd,
                    bizSearchState: location.state?.bizSearchState,
                  },
                })
              }
            >
              가격관리
            </Button>
          </div>
        </div>
      </div>

      <TableWrapper>
        <DataTable
          columns={columns}
          data={data}
          customStyles={tableStyles}
          pagination={true}
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
          defaultSortAsc={false}
          noDataComponent={<NoDataComponent />}
        />
      </TableWrapper>
    </>
  )
}

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 700px;
`
export default PriceHistMgmt
